/**
 * TriggerRuleEditorModal — create / edit a TriggerRule binding.
 *
 * Post-#1184 (Slice 1 of meta #1183) the modal is a thin shell around
 * `TriggerRuleEditorForm`. The actual form body, validation, and
 * persistence logic live in the form component so the App Integrations
 * tab's per-variant drawer can mount the same editor inline, without a
 * nested modal-inside-drawer surface. Two surfaces still use this modal:
 *
 *   1. TriggerRulesForRepo (workspace-settings, repo-centric) — opens
 *      the modal with `presetRepo` carrying integrationId + repoIdentifier.
 *   2. TriggersSection (template-centric, inside ReplayTemplateDialog) —
 *      opens with `presetTemplate` carrying templateId + variantId.
 *
 * The third surface — the variant Automation page that previously
 * mounted this modal — was retired in the same slice; its trigger-rule
 * editor moved into the App Integrations drawer.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui'
import {
  TriggerRuleEditorForm,
  type TriggerRuleEditorFormProps,
} from '@/components/TriggerRuleEditorForm'

export interface TriggerRuleEditorModalProps
  extends Omit<TriggerRuleEditorFormProps, 'onCancel' | 'compact'> {
  onClose: () => void
}

export function TriggerRuleEditorModal({
  initialRule,
  presetRepo,
  presetTemplate,
  onClose,
  onSaved,
}: TriggerRuleEditorModalProps) {
  const title = initialRule ? 'Edit trigger rule' : 'Add trigger rule'
  const subtitle = presetRepo
    ? presetRepo.repoIdentifier
    : initialRule?.repoIdentifier ?? ''

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[580px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {subtitle && (
            <p className="text-xs text-muted-foreground font-mono mt-0.5">
              {subtitle}
            </p>
          )}
        </DialogHeader>
        <DialogBody>
          <TriggerRuleEditorForm
            initialRule={initialRule}
            presetRepo={presetRepo}
            presetTemplate={presetTemplate}
            onCancel={onClose}
            onSaved={onSaved}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
